import { getExternalPlatformDebugOverride } from '../externalPlatform/debug';

import { ltiContextManager } from './LtiContextManager';

/**
 * Single source of truth for client-side LTI detection.
 *
 * Detection sources (in order):
 *   1. LTI route path (`/lti/*`)
 *   2. sessionStorage (set by /lti/deep-linking page)
 *   3. URL query param `ltiUserId` (set by /lti/launch iframe wrapper)
 *
 * Both hooks (`useIsInLti`) and non-hook callsites (`isInLtiContext`) delegate
 * here so behavior stays consistent.
 */

export const LTI_USER_ID_QUERY_PARAM = 'ltiUserId';

/** Custom event fired by LtiContextManager after mutations, so hooks can resync. */
export const LTI_CONTEXT_UPDATE_EVENT = 'zep:lti-context-update';

function hasLtiUserIdInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has(
    LTI_USER_ID_QUERY_PARAM,
  );
}

function hasLtiRoutePath(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.location.pathname === '/lti' ||
    window.location.pathname.startsWith('/lti/')
  );
}

export function detectIsInLti(): boolean {
  if (typeof window === 'undefined') return false;
  const debugOverride = getExternalPlatformDebugOverride();
  if (debugOverride) return debugOverride.mode === 'lti';
  return (
    hasLtiRoutePath() || ltiContextManager.isInLti() || hasLtiUserIdInUrl()
  );
}

export function detectIsLtiInstructor(): boolean {
  if (typeof window === 'undefined') return false;
  const debugOverride = getExternalPlatformDebugOverride();
  if (debugOverride?.mode === 'lti') {
    return debugOverride.role === 'instructor';
  }
  return ltiContextManager.isInstructor();
}
